import { growthBook } from "@/helpers/growthbook";

export const SUBSCRIPTION_CONTROL_PLAN_NAME = "Delivery every 4 Months";

/** price-discount-test: 0 = control, 1 = 15% off selling plan */
export const SUBSCRIPTION_PLAN_NAME_BY_VARIATION: Record<number, string> = {
  0: SUBSCRIPTION_CONTROL_PLAN_NAME,
  1: "Delivery every 4 Months - 15",
};

/** Canonical percent for the `Delivery every 4 Months - 15` plan (Shopify may expose ~23.5 from allocations). */
export const SUBSCRIPTION_15_PLAN_DISCOUNT_PERCENT = 15;

/**
 * Build variation → discount map from Liquid/native card JSON (`selling_plan_allocations`).
 */
export function getSubscriptionDiscountsByVariationFromLiquidAllocations(
  allocations:
    | ReadonlyArray<{
        selling_plan_name?: string;
        discount_percent?: number | null;
      }>
    | undefined
): Partial<Record<number, number>> | undefined {
  if (!allocations?.length) return undefined;
  const plans = allocations
    .filter(
      (
        a
      ): a is typeof a & {
        selling_plan_name: string;
        discount_percent: number;
      } =>
        typeof a.selling_plan_name === "string" &&
        typeof a.discount_percent === "number" &&
        a.discount_percent > 0
    )
    .map((a) => ({
      name: a.selling_plan_name,
      discount: { kind: "percentage" as const, percentage: a.discount_percent },
    }));
  return getSubscriptionDiscountsByVariationFromPlans(plans, {
    getName: (p) => p.name,
    getPercent: (p) => p.discount.percentage,
  });
}

export function getSubscriptionDiscountVariation(): number {
  try {
    const variation = growthBook.getFeatureValue("price-discount-test", 0);
    return Number.isFinite(Number(variation)) ? Number(variation) : 0;
  } catch (_e) {
    return 0;
  }
}

/**
 * Returns the plan name to use for the active variation.
 * Variation switching only applies when control discount is exactly 25%.
 */
export function getSubscriptionPlanNameForVariation(
  controlDiscountPercent?: number
): string {
  if (controlDiscountPercent !== 25) {
    return SUBSCRIPTION_CONTROL_PLAN_NAME;
  }

  const variation = getSubscriptionDiscountVariation();
  return (
    SUBSCRIPTION_PLAN_NAME_BY_VARIATION[variation] ||
    SUBSCRIPTION_CONTROL_PLAN_NAME
  );
}

export function getSubscriptionDiscountsByVariationFromPlans<T>(
  plans: T[] | undefined,
  options: {
    getName: (plan: T) => string | undefined;
    getPercent: (plan: T) => number | undefined;
  }
): Partial<Record<number, number>> | undefined {
  if (!Array.isArray(plans) || plans.length === 0) return undefined;

  const discounts = Object.entries(SUBSCRIPTION_PLAN_NAME_BY_VARIATION).reduce<
    Partial<Record<number, number>>
  >((acc, [variationKey, expectedName]) => {
    const variation = Number(variationKey);
    const plan = plans.find((candidate) => {
      const name = options.getName(candidate);
      return name === expectedName;
    });
    if (!plan) return acc;

    const percent = Number(options.getPercent(plan));
    if (Number.isFinite(percent) && percent > 0) {
      acc[variation] = percent;
    }
    return acc;
  }, {});

  return Object.keys(discounts).length > 0 ? discounts : undefined;
}
